<template>
  <ContentSection>
    <Container indent="wide">
      <!-- TITLE -->
      <h2
        v-if="data.heading"
        class="default-component-title mb-8 sm:mb-10"
        v-html="$replaceHtmlTags(data.heading)"
      />

      <!-- Breadcrumbs -->
      <DownloadsAreaBreadcrumbs
        ref="scrollContainer"
        :breadcrumbs="breadcrumbs"
        class="mb-6 lg:mb-10"
      >
        {{ $t('downloads') }}
      </DownloadsAreaBreadcrumbs>

      <p v-if="structure" class="mb-4 text-sm text-black/60">
        {{ $t('entries', structure.length) }}
      </p>
      <AnimatedContainer :key="structure">
        <ul class="flex flex-col space-y-2">
          <li v-for="entry in structure" :key="entry.entry?.id || entry.id">
            <div class="fade-in">
              <FolderOrFile :data="entry.entry || entry" />
            </div>
          </li>
        </ul>
      </AnimatedContainer>
    </Container>
  </ContentSection>
</template>

<script setup>
const props = defineProps({
  data: {
    type: Object,
    required: true,
  },
})

const queryScope = `downloads-area-specific-${props.data.id}`
provide('queryScope', queryScope)

const route = useRoute()
const breadcrumbs = ref([])
const scrollContainer = ref(null)

const entries = ref([])
const structure = ref([])

async function fetchEntries() {
  const response = await useAsyncGql({
    operation: 'DownloadsAreaSpecific',
    variables: {
      filter: {
        id: {
          in: entryIds.value,
        },
      },
    },
  })

  entries.value = response?.data?.value?.entries.data

  if (!entries.value) {
    return
  }

  // Sort the Entries in the same order as the given entryIds
  entries.value.sort((a, b) => {
    return entryIds.value.indexOf(a.id) - entryIds.value.indexOf(b.id)
  })

  structure.value = [...response?.data?.value?.entries.data]
}

// FUNCTIONS
function navigateToFolder(query) {
  // If level is 0, return the root level
  const queryDownloadArea = query[queryScope]
    ? JSON.parse(query[queryScope])
    : { level: 0 }

  if (parseInt(queryDownloadArea.level) === 0) {
    structure.value = entries.value
    breadcrumbs.value = []
    return
  }

  // If level is not 0, search for the folder in the nested levels
  structure.value =
    getFolderBySlug(
      entries.value,
      queryDownloadArea.level,
      queryDownloadArea.id
    )?.children || []

  // Retrieve the breadcrumb path for the folder with the given id
  breadcrumbs.value = getFolderBreadcrumb(entries.value, queryDownloadArea.id)

  if (scrollContainer.value) {
    setScrollPosition()
  }
}

function getFolderBySlug(array, level, id = null) {
  const actualLevel = parseInt(level) - 1
  // if level 0 and no id is given, return the root level
  if (actualLevel === 0 && !id) {
    return array
  }

  if (actualLevel === 0) {
    // If level is 0, search for the folder in the root level
    return array.find((item) => item.entry?.id === id || item.id === id)
  }

  // Recursive function to search for the folder in nested levels
  function findFolderInLevel(items, currentLevel) {
    for (const item of items) {
      if (item.children && item.children.length > 0) {
        const foundFolder = findFolderInLevel(item.children, currentLevel - 1)
        if (foundFolder) {
          return foundFolder
        }
      }
      if ((item.id === id || item.entry?.id === id) && currentLevel === 0) {
        return item
      }
    }
    return null
  }

  // Call the recursive function to search for the folder in the desired level
  return findFolderInLevel(array, actualLevel)
}

function getFolderBreadcrumb(array, id) {
  // return empty array if no id is given
  if (!id) {
    return []
  }

  const breadcrumb = []

  function findFolder(items, currentLevel, currentBreadcrumb) {
    for (const item of items) {
      const folderPath = [
        ...currentBreadcrumb,
        {
          level: currentLevel + 1,
          id: item.entry?.id || item.id,
          title: item.entry?.title || item.title,
        },
      ]

      if (item.entry?.id === id || item.id === id) {
        breadcrumb.push(...folderPath)
        return
      }

      if (item.children && item.children.length > 0) {
        findFolder(item.children, currentLevel + 1, folderPath)
      }
    }
  }

  findFolder(array, 0, [])

  return breadcrumb
}

function setScrollPosition() {
  scrollContainer.value.setScrollPosition()
}

// COMPUTED
const entryIds = computed(() => {
  return props.data?.entries?.map((entry) => entry.id)
})

// WATCHERS
watch(
  () => route.query,
  async (query) => {
    await nextTick()
    navigateToFolder(query)
  }
)

// MOUNTED
onMounted(() => {
  setScrollPosition()
})

await fetchEntries()
navigateToFolder(route.query)
</script>
